import { useQuery, useMutation } from '@tanstack/react-query'
import { PropertiesFilterParams, CreatePropertyParams, UpdatePropertyParams } from '@/types/property'
import { usePropertiesStore } from '@/store'
import { SortOrderEnum } from '@/enums'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useProperties = (params: PropertiesFilterParams) => {
  const fetchProperties = usePropertiesStore(state => state.fetchProperties)

  return useQuery({
    queryKey: ['properties', params],
    queryFn: () => fetchProperties(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useProperty = (messageId: string) => {
  const getProperty = usePropertiesStore(state => state.getProperty)

  return useQuery({
    queryKey: ['property', messageId],
    queryFn: () => getProperty(messageId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!messageId
  })
}

export const useCreateProperty = () => {
  const createProperty = usePropertiesStore(state => state.createProperty)

  return useMutation({
    mutationFn: (params: CreatePropertyParams) => createProperty(params),
    onSuccess: () => {},
    throwOnError: false,
    meta: {
      errorMessage: false
    }
  })
}

export const useUpdateProperty = () => {
  const updateProperty = usePropertiesStore(state => state.updateProperty)

  return useMutation({
    mutationFn: ({ propertyId, params }: { propertyId: string; params: UpdatePropertyParams }) =>
      updateProperty(propertyId, params),
    onSuccess: () => {},
    throwOnError: false,
    meta: {
      errorMessage: false
    }
  })
}

export const useSortProperty = () => {
  const sortProperty = usePropertiesStore(state => state.sortProperty)

  return useMutation({
    mutationFn: ({ propertyId, type }: { propertyId: string; type: SortOrderEnum }) => sortProperty(propertyId, type),
    onSuccess: () => {}
  })
}

export const useDeleteProperty = () => {
  const deleteProperty = usePropertiesStore(state => state.deleteProperty)

  return useMutation({
    mutationFn: (messageId: string) => deleteProperty(messageId),
    onSuccess: () => {},
    throwOnError: false
  })
}

export const useBulkDeleteProperties = () => {
  const bulkDeleteProperties = usePropertiesStore(state => state.bulkDeleteProperties)
  const deselectAllProperties = usePropertiesStore(state => state.deselectAllProperties)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteProperties(ids),
    onSuccess: response => {
      deselectAllProperties()
      handleBulkDeleteSuccess(response)
    }
  })
}
